import Nope from "nope-validator";
import { FiEdit3, FiPlusCircle, FiTrash2 } from "react-icons/fi";
import { useForm } from "react-hook-form";
import axios, { AxiosResponse } from "axios";
import { nopeResolver } from "@hookform/resolvers/nope";
import { useCallback, useEffect, useState } from "react";
import { InventoryItemObject, Item, NotificationType } from "react-app-env";

import { API_URL } from "@redux/api";
import Accordion from "@components/common/Accordion";
import MessageError from "@components/form/MessageError"
import TextInput from "@components/form/TextInput"
import Button from "@components/common/Button";
import Notification from "@components/common/Notification";
import AddNewItemForm from "@components/dataBarang/AddNewItemForm";
import Modal from "@components/common/Modal";
import EditItemForm from "@components/dataBarang/EditItemForm";
import { EditItemFormType } from "@redux/validations/createItems";
import DeleteItem from "@components/dataBarang/DeleteItem";
import { useParams } from "react-router-dom";

type SearchInput = {
	searchName: string;
};

type FiltersType = {
  id: string;
  value: string;
}[];

const schemaLogin = Nope.object().shape({
	email: Nope.string().email('Masukkan email dengan benar').required('Masukkan email'),
	password: Nope.string().required('Masukkan password'),
});

const DataBarangListRoute = () => {
  const { id } = useParams();

  const [listItems, setListItems] = useState<InventoryItemObject | null>(null);
  const [accordionOpened, setAccordionOpened] = useState<string[] | []>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
	const [notification, setNotification] = useState<NotificationType | null>(null);

  const [isOpenAddItemModal, setIsOpenAddItemModal] = useState<boolean>(false);
  const [isOpenEditItemModal, setIsOpenEditItemModal] = useState<boolean>(false);
  const [isOpenDeleteItemModal, setIsOpenDeleteItemModal] = useState<boolean>(false);

  const [selectedItem, setSelectedItem] = useState<EditItemFormType | null>(null)

	const {
		register,
    watch,
    // getValues,
		// setError,
		formState: { errors },
	} = useForm<SearchInput>({
		defaultValues: {
			searchName: ""
		},
		resolver: nopeResolver(schemaLogin),
	});

  const fetchItems = useCallback(async({searchName, filters}: {searchName: string, filters: FiltersType}) => {
    setIsLoading(true);
    setNotification(null);

    const payload = { searchName, filters };
    const response: AxiosResponse = await axios.post(`${API_URL}/inventory/items`, 
      payload
    );

    const { status, data } = response;

    if(status === 200) {
      setListItems(data.data);
      setIsLoading(false);
    } else {
      setNotification({
        isError: true,
        title: "Error",
        text: data.message,
      });
      setIsLoading(false);
    };
  }, []);

  const searchName = watch("searchName");

  useEffect(() => {
    let filters: FiltersType = [];
    if(id) filters = [{ id: "category", value: id }];
    if(!searchName) fetchItems({searchName, filters});
    return () => {};
  }, [searchName, fetchItems, id]);

  const collapseHandler = (value: string) => {
		if(accordionOpened.some(el => el === value)) {
			setAccordionOpened(accordionOpened.filter(el => el !== value));
		} else {
			setAccordionOpened([...accordionOpened, value]);
		};
	};

  const addNewItemHandler = () => {
    setIsOpenAddItemModal(true);
    setIsOpenEditItemModal(false);
    setIsOpenDeleteItemModal(false);
  };
  
  const editButtonHandler = (itemData: EditItemFormType) => {
    setSelectedItem(itemData);
    setIsOpenEditItemModal(true);
    setIsOpenAddItemModal(false);
    setIsOpenDeleteItemModal(false)
  };

  const deleteButtonHandler = (itemData: EditItemFormType) => {
    setSelectedItem(itemData);
    setIsOpenDeleteItemModal(true);
    setIsOpenEditItemModal(false);
    setIsOpenAddItemModal(false);
  };

  return (
    <div>
      <div className="bg-white rounded-xl p-4">
        <h1 className="text-center text-xl md:text-2xl lg:text-4xl font-semibold">List Barang Gudang</h1>
      </div>

      <div className="mt-8">
        <div className="flex items-end gap-5 justify-between">
          <TextInput 
            label="Cari Barang"
            labelClass=""
          >
            <input
              type='text'
              {...register('searchName')}
              aria-invalid={!!errors?.searchName}
              placeholder="Masukkan nama barang"
              className="px-2 py-4 border-none"
            />
						<MessageError error={errors?.searchName?.message} />
          </TextInput>
          <Button
            onClick={() => {
              let filters: FiltersType = [];
              if(id) filters = [{ id: "category", value: id }];

              fetchItems({searchName, filters})
            }}
          >
            Cari
          </Button>
        </div>
      </div>

      <div className="mt-10">
        <button 
          className="w-full bg-sky-400 p-4 rounded-xl flex items-center justify-center gap-2 text-white"
          onClick={() => addNewItemHandler()}
        >
          <FiPlusCircle />
          Tambah Barang Baru
        </button>
      </div>

      <div className="mt-10">
        { watch("searchName") !== "" && !isLoading && 
          <div className="mb-5">
            <h2 className="font-semibold">Hasil Pencarian :</h2>
          </div>
        }

        <Notification notification={notification} />

        <div className=''>
          { isLoading ? null 
            : !listItems ? 
              <div>
                <h2>Tidak ada barang untuk ditampilkan</h2>
              </div>
            : Object.keys(listItems).map(key => {
              const items: Item[] = listItems[key];
              const label = key.charAt(0).toUpperCase() + key.slice(1);

              return (
                <div key={key} className="bg-white mb-4 rounded-xl overflow-hidden">
                  <Accordion
                    label={label}
                    isCollapsed={accordionOpened.some(el => el === key)}
                    collapseHandler={() => collapseHandler(key)}
                    childrenWrapperClass="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                  >
                    {items.length > 0 ? items.map(item => {
                      return (
                        <div key={item._id} className="bg-white p-4 border-t">
                          <div className="md:min-h-[245px] flex items-center">
                            <img
                              src={`${API_URL}/images/inventories/${item.category.name}/${item.image}`}
                              alt={item.name}
                              className="w-1/2 h-auto mx-auto md:h-[245px] md:w-auto"
                              onError={({ currentTarget }) => {
                                currentTarget.onerror = null; // prevents looping
                                currentTarget.src=`${API_URL}/images/no-image.webp`;
                              }}
                            />
                          </div>
                          <h3 className="text-xl mt-4 mb-1 font-semibold capitalize md:text-center">{item.name}</h3>
                          <div className="flex items-center justify-between">
                            <span>Kode Barang : <strong>{item.code || "-"}</strong></span>
                            <span>Jumlah : <strong>{item.quantity} {item.unit}</strong></span>
                          </div>
                          <div>
                            <span>Lokasi Barang : <strong>{item.location || "-"}</strong></span>
                          </div>
                          <div className="mt-3 flex gap-2">
                            <button 
                              className="grow bg-orange-500 py-2 rounded-xl text-white text-lg flex items-center gap-2 justify-center"
                              onClick={() => editButtonHandler(item)}
                            >
                              <FiEdit3 />
                              Edit
                            </button>
                            <button 
                              className="grow-0 bg-red-500 px-4 py-2 rounded-xl text-white text-lg flex items-center gap-2 justify-center"
                              onClick={() => deleteButtonHandler(item)}
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </div>
                      )
                    }) : null}
                  </Accordion>
                </div>
              )
            })
          }
        </div>
      </div>
      
      <Modal
        showCloseButton
        title="Form Tambah Barang"
        isOpen={isOpenAddItemModal}
        onClose={() => {
          let filters: FiltersType = [];
          if(id) filters = [{ id: "category", value: id }];
          fetchItems({searchName, filters});
          setIsOpenAddItemModal(false);
        }}
      >
        <div className="border-t">
          <AddNewItemForm 
            onClose={() => {
              let filters: FiltersType = [];
          if(id) filters = [{ id: "category", value: id }];
              fetchItems({searchName, filters});
              setIsOpenAddItemModal(false);
            }}
          />
        </div>
      </Modal>

      <Modal
        showCloseButton
        title="Form Edit Barang"
        isOpen={isOpenEditItemModal}
        onClose={() => {
          let filters: FiltersType = [];
          if(id) filters = [{ id: "category", value: id }];
          fetchItems({searchName, filters});
          setSelectedItem(null);
          setIsOpenEditItemModal(false);
        }}
      >
        <div className="border-t">
          <EditItemForm 
            data={selectedItem} 
            onClose={() => {
              let filters: FiltersType = [];
          if(id) filters = [{ id: "category", value: id }];
              fetchItems({searchName, filters});
              setSelectedItem(null);
              setIsOpenEditItemModal(false);
            }}
          />
        </div>
      </Modal>

      <Modal
        showCloseButton
        title="Hapus Data Barang"
        isOpen={isOpenDeleteItemModal}
        onClose={() => {
          let filters: FiltersType = [];
          if(id) filters = [{ id: "category", value: id }];
          fetchItems({searchName, filters});
          setSelectedItem(null);
          setIsOpenDeleteItemModal(false);
        }}
      >
        <div className="border-t pt-5">
          <DeleteItem 
            itemId={selectedItem?._id}
            itemName={selectedItem?.name}
            onCloseModal={() => {
              let filters: FiltersType = [];
          if(id) filters = [{ id: "category", value: id }];
              fetchItems({searchName, filters});
              setSelectedItem(null);
              setIsOpenDeleteItemModal(false);
            }}
          />
        </div>
      </Modal>
    </div>
  )
}

export default DataBarangListRoute